import React, { useState, useEffect, useRef, useCallback } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import config from '../config.js';
import { setAiContext, clearAiContext } from '../utils/aiContext';
import { apiErrorMessage } from '../utils/apiError';
// Reads, the edit PATCH and the task video are shared with the phone job detail
// (features/jobs/, docs/mobile-pwa-plan.md "One copy of every action").
import { loadJob, loadJobTasks, relativeDueLabel, JOB_STATUS_OPTIONS, JOB_PRIORITY_OPTIONS } from '../features/jobs/jobsData';
import { patchJob } from '../features/jobs/jobActions';
import { loadCameraConfig, canShowTaskVideo, taskCameraCount, listTaskClips, clipCount, loadClipUrls, clipTimeCT } from '../features/jobs/taskVideo';
import { canOfferCutSheetSwitch } from '../features/jobs/cutSheetSwitch';
import CutSheetSwitchModal from '../components/CutSheetSwitchModal.jsx';

// Format ISO timestamp to Central Time
function formatTimeCT(isoStr) {
  if (!isoStr) return "";
  const dt = new Date(isoStr);
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true
  }).format(dt);
}

// Status badge with explicit colors for light/dark mode
function StatusBadge({ status }) {
  const colorMap = {
    Completed: { bg: "#198754", color: "#fff" },
    Shipped: { bg: "#198754", color: "#fff" },
    "In Progress": { bg: "#fd7e14", color: "#fff" },
    Paused: { bg: "#6c757d", color: "#fff" },
    Pending: { bg: "#495057", color: "#e9ecef" },
    Cancelled: { bg: "#dc3545", color: "#fff" },
  };
  const s = colorMap[status] || { bg: "#6c757d", color: "#fff" };
  return <span className="badge" style={{ backgroundColor: s.bg, color: s.color, fontSize: "0.85em" }}>{status}</span>;
}

// Task row background colors
const taskRowStyles = {
  Completed: { backgroundColor: "rgba(25, 135, 84, 0.08)" },
  "In Progress": { backgroundColor: "rgba(13, 110, 253, 0.08)" },
  Paused: { backgroundColor: "rgba(255, 193, 7, 0.08)" },
};

export default function JobDetailsPage() {
  const { jobId } = useParams()
  const navigate = useNavigate()

  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  // For inline editing
  const [editMode, setEditMode] = useState(false)
  const [tempDescription, setTempDescription] = useState("")
  const [tempPriority, setTempPriority] = useState(2)
  const [tempStatus, setTempStatus] = useState("Pending")
  const [tempDueDate, setTempDueDate] = useState("")
  const [tempNotes, setTempNotes] = useState("")

  // For job tasks
  const [tasks, setTasks] = useState([])

  // Inline feedback
  const [feedback, setFeedback] = useState(null)
  // Switch cut sheet dialog: the laser task it is open for (null = closed)
  const [switchTaskId, setSwitchTaskId] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(false)

  // --- Video playback state ---
  const [cameraConfig, setCameraConfig] = useState(null) // { station: count }
  const [videoTask, setVideoTask] = useState(null) // task being viewed
  const [clipsByCamera, setClipsByCamera] = useState({}) // { 0: ["file1.mp4", ...], 1: [...] }
  const [currentClipIdx, setCurrentClipIdx] = useState(0)
  const [videoUrls, setVideoUrls] = useState({}) // { 0: blobUrl, 1: blobUrl }
  const [videoLoading, setVideoLoading] = useState(false)
  const [videoError, setVideoError] = useState("")
  const videoRefs = useRef({}) // { 0: videoElement, 1: videoElement }
  const blobUrlsRef = useRef({}) // track blob URLs for cleanup

  const API_BASE = config.API_BASE_URL;

  // Clear AI context on unmount
  useEffect(() => () => clearAiContext(), []);

  // Auto-dismiss feedback
  useEffect(() => {
    if (feedback) {
      const timer = setTimeout(() => setFeedback(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [feedback]);

  // Auto-revert confirmDelete
  useEffect(() => {
    if (confirmDelete) {
      const timer = setTimeout(() => setConfirmDelete(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [confirmDelete]);

  // Fetch camera config on mount
  useEffect(() => {
    loadCameraConfig().then(data => { if (data) setCameraConfig(data); }) // relay down: no video icons
  }, [])

  // Load job info
  const fetchJob = useCallback(() => {
    setLoading(true)
    setError(null)
    loadJob(jobId)
      .then((data) => {
        setJob(data)
        setAiContext('job', { jobId, masterJobNum: data.master_job_num, jobDescription: data.job_description });
        // if using edit mode, store fields in temp states
        setTempDescription(data.job_description || "")
        setTempPriority(data.priority_level || 2)
        setTempStatus(data.status || "Pending")
        if (data.due_date) {
          // remove time portion if data.due_date is an ISO string
          setTempDueDate(data.due_date.split("T")[0])
        } else {
          setTempDueDate("")
        }
        setTempNotes(data.notes || "")
        setLoading(false)
      })
      .catch((err) => {
        console.error(err)
        setError(err.message)
        setLoading(false)
      })
  }, [jobId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Load tasks from /jobs/{jobId}/progress
  const fetchTasks = useCallback(() => {
    loadJobTasks(jobId)
      .then((t) => {
        if (t) {
          setTasks(t)
        }
      })
      .catch((err) => {
        console.error("Error fetching tasks:", err)
      })
  }, [jobId])

  useEffect(() => {
    fetchTasks()
  }, [fetchTasks])

  // --- Video modal helpers ---

  // Revoke all current blob URLs
  const revokeAllBlobs = useCallback(() => {
    Object.values(blobUrlsRef.current).forEach(url => {
      try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
    });
    blobUrlsRef.current = {};
    setVideoUrls({});
  }, []);

  // Fetch clips for all cameras and load the first set
  const openVideoModal = useCallback(async (task) => {
    if (!cameraConfig) return;
    const camCount = taskCameraCount(task, cameraConfig);
    if (camCount === 0) return;

    setVideoTask(task);
    setVideoError("");
    setVideoLoading(true);
    setCurrentClipIdx(0);
    revokeAllBlobs();

    try {
      // Every camera's clip list for the task's time range (features/jobs/taskVideo.js)
      const allClips = await listTaskClips(task, camCount);

      setClipsByCamera(allClips);

      // Check if we have any clips at all
      const totalClips = clipCount(allClips);
      if (totalClips === 0) {
        setVideoError("No recordings found for this task's time range.");
        setVideoLoading(false);
        return;
      }

      // Load first set of clips
      await loadClipBlobs(allClips, 0, task.workstation);
    } catch (err) {
      console.error("Error fetching clips:", err);
      setVideoError("Failed to fetch recordings.");
    } finally {
      setVideoLoading(false);
    }
  }, [cameraConfig, revokeAllBlobs]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load blob URLs for a given clip index across all cameras
  const loadClipBlobs = async (clips, clipIdx, station) => {
    revokeAllBlobs();
    setVideoLoading(true);
    setVideoError("");

    const stationName = station || videoTask?.workstation || "";

    try {
      // Each camera's clip as a blob: URL; a camera that fails is left out (features/jobs/taskVideo.js)
      const newUrls = await loadClipUrls(clips, clipIdx, stationName);
      const newBlobRefs = { ...newUrls };

      if (Object.keys(newUrls).length === 0) {
        setVideoError("Failed to load video clips.");
      }

      blobUrlsRef.current = newBlobRefs;
      setVideoUrls(newUrls);
    } catch (err) {
      setVideoError("Failed to load video clips.");
    } finally {
      setVideoLoading(false);
    }
  };

  // Navigate clips
  const maxClipCount = clipCount(clipsByCamera);

  const goToClip = useCallback(async (newIdx) => {
    if (newIdx < 0 || newIdx >= maxClipCount) return;
    setCurrentClipIdx(newIdx);
    await loadClipBlobs(clipsByCamera, newIdx);
  }, [clipsByCamera, maxClipCount]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-advance when primary video ends
  const handleVideoEnded = useCallback(() => {
    if (currentClipIdx < maxClipCount - 1) {
      goToClip(currentClipIdx + 1);
    }
  }, [currentClipIdx, maxClipCount, goToClip]);

  // Close modal and cleanup
  const closeVideoModal = useCallback(() => {
    setVideoTask(null);
    setClipsByCamera({});
    setCurrentClipIdx(0);
    setVideoError("");
    setVideoLoading(false);
    revokeAllBlobs();
    videoRefs.current = {};
  }, [revokeAllBlobs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      Object.values(blobUrlsRef.current).forEach(url => {
        try { URL.revokeObjectURL(url); } catch (e) { /* noop */ }
      });
    };
  }, []);

  // Check if a task can show video
  const canShowVideo = (task) => canShowTaskVideo(task, cameraConfig);

  if (loading) {
    return <div className="p-3">Loading job #{jobId}...</div>
  }

  if (error) {
    return (
      <div className="p-3">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/jobs">Jobs</Link></li>
            <li className="breadcrumb-item active">Error</li>
          </ol>
        </nav>
        <div className="alert alert-danger">
          <p className="mb-2">Error: {error}</p>
          <Link to="/jobs" className="btn btn-secondary btn-sm">Back to Jobs</Link>
        </div>
      </div>
    )
  }

  if (!job) {
    return (
      <div className="p-3">
        <nav aria-label="breadcrumb" className="mb-3">
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><Link to="/jobs">Jobs</Link></li>
            <li className="breadcrumb-item active">Not Found</li>
          </ol>
        </nav>
        <h2>Job Not Found</h2>
        <p>Job ID {jobId} was not found in the database.</p>
        <Link to="/jobs" className="btn btn-secondary">Back to Jobs</Link>
      </div>
    )
  }

  // Switch into edit mode
  const handleEnableEdit = () => {
    setEditMode(true)
  }

  // Cancel out of edit mode
  const handleCancelEdit = () => {
    setEditMode(false)
    setTempDescription(job.job_description || "")
    setTempPriority(job.priority_level || 2)
    setTempStatus(job.status || "Pending")
    setTempDueDate(job.due_date ? job.due_date.split("T")[0] : "")
    setTempNotes(job.notes || "")
  }

  // Save partial updates with PATCH /jobs/{jobId}
  const handleSaveEdit = async () => {
    try {
      const patchBody = {
        job_description: tempDescription,
        priority_level: Number(tempPriority),
        status: tempStatus,
        due_date: tempDueDate, // e.g. "2025-04-10"
        notes: tempNotes
      }
      await patchJob(jobId, patchBody)
      // Once saved, re-fetch job or update local state
      const updatedJob = {
        ...job,
        job_description: tempDescription,
        priority_level: Number(tempPriority),
        status: tempStatus,
        due_date: tempDueDate ? `${tempDueDate}T00:00:00` : null,
        notes: tempNotes
      }
      setJob(updatedJob)
      setEditMode(false)
      setFeedback({ type: "success", message: "Job updated successfully!" })
    } catch (err) {
      setFeedback({ type: "danger", message: `Failed to update job: ${err.message}` })
    }
  }

  // Confirm shipping from details page
  const handleConfirmShipping = async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${jobId}/confirm_shipping`, {
        method: "POST",
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      setFeedback({ type: "success", message: "Job shipped successfully!" })
      fetchJob()
    } catch (err) {
      setFeedback({ type: "danger", message: `Cannot ship job ${jobId}: ${err.message}` })
    }
  }

  // Delete job from details
  const handleDeleteJob = async () => {
    try {
      const res = await fetch(`${API_BASE}/jobs/${jobId}`, {
        method: "DELETE",
      })
      if (!res.ok) {
        throw new Error(await apiErrorMessage(res));
      }
      setFeedback({ type: "success", message: `Job ${jobId} deleted.` })
      setTimeout(() => navigate("/jobs"), 1000)
    } catch (err) {
      setFeedback({ type: "danger", message: `Cannot delete job ${jobId}: ${err.message}` })
    }
  }

  // Can this job be shipped?
  const canShip = job.status !== "Shipped" && job.status !== "Cancelled";

  // Due date info
  const dueDateFormatted = job.due_date ? job.due_date.split("T")[0] : null;
  const relativeDate = job.due_date ? relativeDueLabel(job.due_date) : null;

  // Priority labels
  const priorityLabels = Object.fromEntries(JOB_PRIORITY_OPTIONS.map((p) => [p.value, p.label]));

  // Get camera count for video task
  const videoCamCount = videoTask ? (cameraConfig?.[videoTask.workstation] || 0) : 0;

  // Rendering
  return (
    <div className="p-3">
      {/* Breadcrumb */}
      <nav aria-label="breadcrumb" className="mb-3">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><Link to="/jobs">Jobs</Link></li>
          <li className="breadcrumb-item active">Job #{job.job_id}</li>
        </ol>
      </nav>

      {/* Inline feedback alert */}
      {feedback && (
        <div className={`alert alert-${feedback.type} alert-dismissible fade show`} role="alert">
          {feedback.message}
          <button type="button" className="btn-close" onClick={() => setFeedback(null)}></button>
        </div>
      )}

      {/* Job Detail Card */}
      <div className="card job-detail-card mb-4">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <h5 className="mb-0">Job #{job.job_id}</h5>
            <StatusBadge status={job.status} />
          </div>
          <div className="d-flex gap-2">
            {!editMode ? (
              <>
                <button className="btn btn-outline-primary btn-sm" onClick={handleEnableEdit}>
                  <i className="fas fa-edit me-1"></i>Edit
                </button>
                {canShip && (
                  <button className="btn btn-success btn-sm" onClick={handleConfirmShipping}>
                    <i className="fas fa-shipping-fast me-1"></i>Ship
                  </button>
                )}
                {!confirmDelete ? (
                  <button className="btn btn-outline-danger btn-sm" onClick={() => setConfirmDelete(true)}>
                    <i className="fas fa-trash me-1"></i>Delete
                  </button>
                ) : (
                  <>
                    <button className="btn btn-danger btn-sm" onClick={handleDeleteJob}>
                      Confirm Delete?
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={() => setConfirmDelete(false)}>
                      Cancel
                    </button>
                  </>
                )}
              </>
            ) : (
              <>
                <button className="btn btn-success btn-sm" onClick={handleSaveEdit}>
                  <i className="fas fa-check me-1"></i>Save
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit}>
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="card-body">
          <div className="row">
            {/* Left column: Description + Notes */}
            <div className="col-md-8">
              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Description</label>
                {!editMode ? (
                  <div className="fs-5">{job.job_description || "N/A"}</div>
                ) : (
                  <input
                    className="form-control"
                    value={tempDescription}
                    onChange={(e) => setTempDescription(e.target.value)}
                  />
                )}
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Notes</label>
                {!editMode ? (
                  <div style={{ whiteSpace: "pre-wrap" }}>{job.notes || "No notes"}</div>
                ) : (
                  <textarea
                    className="form-control"
                    rows={3}
                    value={tempNotes}
                    onChange={(e) => setTempNotes(e.target.value)}
                  />
                )}
              </div>
            </div>

            {/* Right column: Status, Priority, Due Date */}
            <div className="col-md-4">
              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Status</label>
                {!editMode ? (
                  <div><StatusBadge status={job.status} /></div>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={tempStatus}
                    onChange={(e) => setTempStatus(e.target.value)}
                  >
                    {JOB_STATUS_OPTIONS.map((st) => <option key={st} value={st}>{st}</option>)}
                  </select>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Priority</label>
                {!editMode ? (
                  <div>{priorityLabels[job.priority_level] || job.priority_level}</div>
                ) : (
                  <select
                    className="form-select form-select-sm"
                    value={tempPriority}
                    onChange={(e) => setTempPriority(e.target.value)}
                  >
                    {JOB_PRIORITY_OPTIONS.map((p) => <option key={p.value} value={String(p.value)}>{p.label}</option>)}
                  </select>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label text-muted small mb-1">Due Date</label>
                {!editMode ? (
                  <div>
                    <div>{dueDateFormatted || "N/A"}</div>
                    {relativeDate && (
                      <small style={{ color: relativeDate.color, fontWeight: 500 }}>
                        {relativeDate.text}
                      </small>
                    )}
                  </div>
                ) : (
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={tempDueDate}
                    onChange={(e) => setTempDueDate(e.target.value)}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks Section */}
      <h5 className="mb-3">Tasks</h5>
      {tasks.length === 0 ? (
        <div className="text-muted">No tasks found for this job.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-hover table-sm">
            <thead>
              <tr>
                <th>Task ID</th>
                <th>Workstation</th>
                <th>Status</th>
                <th>Quantity</th>
                <th>Sheet Required</th>
                <th style={{ width: '50px' }}></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.task_id} style={taskRowStyles[t.status] || {}}>
                  <td>{t.task_id}</td>
                  <td>{t.workstation}</td>
                  <td><StatusBadge status={t.status} /></td>
                  <td>{t.quantity}</td>
                  <td>{t.sheet_required || 0}</td>
                  <td className="text-center text-nowrap">
                    {canOfferCutSheetSwitch(t, job.status) && (
                      <button
                        className="btn btn-sm btn-outline-warning me-1"
                        title="Cut this job from another cut sheet (the same nest on other steel)"
                        onClick={() => setSwitchTaskId(t.task_id)}
                      >
                        Switch sheet
                      </button>
                    )}
                    {canShowVideo(t) && (
                      <button
                        className="btn btn-sm btn-outline-dark"
                        title="View task video"
                        onClick={() => openVideoModal(t)}
                      >
                        <i className="fas fa-video"></i>
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <CutSheetSwitchModal
        show={switchTaskId != null}
        taskId={switchTaskId}
        onHide={() => setSwitchTaskId(null)}
        onSwitched={fetchTasks}
      />

      {/* --- Video Playback Modal --- */}
      {videoTask && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}>
          <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: '95vw' }}>
            <div className="modal-content bg-dark text-white border-secondary">
              <div className="modal-header border-secondary">
                <h5 className="modal-title">
                  <i className="fas fa-video me-2 text-danger"></i>
                  Task Video — {videoTask.workstation} — Job #{jobId}
                </h5>
                <div className="text-muted ms-3" style={{ fontSize: '0.85rem' }}>
                  {formatTimeCT(videoTask.start_time)} — {formatTimeCT(videoTask.end_time)} CT
                </div>
                <button type="button" className="btn-close btn-close-white" onClick={closeVideoModal}></button>
              </div>

              <div className="modal-body" style={{ minHeight: '50vh' }}>
                {videoLoading && Object.keys(videoUrls).length === 0 ? (
                  <div className="text-center p-5">
                    <div className="spinner-border text-light mb-3" role="status"></div>
                    <div className="text-muted">Loading recordings...</div>
                  </div>
                ) : videoError && Object.keys(videoUrls).length === 0 ? (
                  <div className="text-center p-5">
                    <div className="text-warning mb-3">
                      <i className="fas fa-exclamation-triangle me-2"></i>
                      {videoError}
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Video grid */}
                    <div className="d-flex flex-wrap gap-2 justify-content-center">
                      {Array.from({ length: videoCamCount }, (_, camIdx) => (
                        <div key={camIdx} className="text-center" style={{ flex: videoCamCount <= 2 ? '1 1 48%' : '1 1 31%', maxWidth: videoCamCount === 1 ? '100%' : videoCamCount === 2 ? '49%' : '33%' }}>
                          <div className="mb-1" style={{ fontSize: '0.8rem', color: '#aaa' }}>
                            Camera {camIdx + 1}
                          </div>
                          {videoUrls[camIdx] ? (
                            <video
                              key={videoUrls[camIdx]}
                              ref={el => { videoRefs.current[camIdx] = el; }}
                              src={videoUrls[camIdx]}
                              controls={camIdx === 0}
                              muted={camIdx !== 0}
                              style={{ width: '100%', backgroundColor: '#000', borderRadius: '4px' }}
                              onPlay={() => {
                                // Sync all other videos
                                Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                  if (parseInt(idx) !== 0 && el && el.paused) {
                                    el.currentTime = videoRefs.current[0]?.currentTime || 0;
                                    el.play().catch(() => {});
                                  }
                                });
                              }}
                              onPause={() => {
                                if (camIdx === 0) {
                                  Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                    if (parseInt(idx) !== 0 && el && !el.paused) {
                                      el.pause();
                                    }
                                  });
                                }
                              }}
                              onSeeked={() => {
                                if (camIdx === 0) {
                                  const t = videoRefs.current[0]?.currentTime || 0;
                                  Object.entries(videoRefs.current).forEach(([idx, el]) => {
                                    if (parseInt(idx) !== 0 && el) {
                                      el.currentTime = t;
                                    }
                                  });
                                }
                              }}
                              onEnded={camIdx === 0 ? handleVideoEnded : undefined}
                            />
                          ) : (
                            <div style={{ backgroundColor: '#111', borderRadius: '4px', padding: '3rem 1rem', color: '#666' }}>
                              {videoLoading ? "Loading..." : "No clip"}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Loading overlay for clip transitions */}
                    {videoLoading && Object.keys(videoUrls).length > 0 && (
                      <div className="text-center mt-2">
                        <div className="spinner-border spinner-border-sm text-light me-2" role="status"></div>
                        <small className="text-muted">Loading next clip...</small>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer: clip navigation */}
              {maxClipCount > 0 && (
                <div className="modal-footer border-secondary d-flex justify-content-between align-items-center">
                  <button
                    className="btn btn-sm btn-outline-light"
                    disabled={currentClipIdx === 0 || videoLoading}
                    onClick={() => goToClip(currentClipIdx - 1)}
                  >
                    <i className="fas fa-chevron-left me-1"></i> Previous
                  </button>

                  <div className="d-flex align-items-center gap-2 flex-wrap justify-content-center" style={{ maxWidth: '70%' }}>
                    {(() => {
                      // Use camera 0's clip list for timeline labels
                      const cam0Clips = clipsByCamera[0] || [];
                      // Show pills for each clip, but limit to avoid overflow
                      const maxPills = 20;
                      const showAll = cam0Clips.length <= maxPills;
                      const displayed = showAll ? cam0Clips : cam0Clips;

                      if (cam0Clips.length === 0) {
                        return <span className="text-muted">No clips</span>;
                      }

                      return (
                        <>
                          <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                            Clip {currentClipIdx + 1} / {maxClipCount}
                          </span>
                          {showAll && displayed.map((f, i) => (
                            <button
                              key={f}
                              className={`btn btn-sm ${i === currentClipIdx ? 'btn-danger' : 'btn-outline-secondary'}`}
                              style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem' }}
                              disabled={videoLoading}
                              onClick={() => goToClip(i)}
                            >
                              {clipTimeCT(f)}
                            </button>
                          ))}
                          {!showAll && (
                            <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                              {clipTimeCT(cam0Clips[currentClipIdx] || "")}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <button
                    className="btn btn-sm btn-outline-light"
                    disabled={currentClipIdx >= maxClipCount - 1 || videoLoading}
                    onClick={() => goToClip(currentClipIdx + 1)}
                  >
                    Next <i className="fas fa-chevron-right ms-1"></i>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
